import React, { useState, useEffect } from "react";
import { closeAddUpiModal } from "../../features/slices/ProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import { addUpiDetails, reset } from "../../features/slices/PaymentSlice";
import { useMediaQuery } from "react-responsive";

const UPI_ID_REGEX = /^[\w.\-]{2,}@[a-zA-Z]{2,}$/;
const ALPHA_NAME_REGEX = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/webp",
];

const validateForm = ({
  upiId,
  accountName,
  accountHolderName,
  qrCodeUrl,
  file,
}) => {
  if (!upiId.trim()) {
    return "Please enter UPI ID";
  }
  if (!UPI_ID_REGEX.test(upiId.trim())) {
    return "UPI ID is not valid (e.g. user@upi)";
  }
  if (accountName.trim() && !ALPHA_NAME_REGEX.test(accountName.trim())) {
    return "Account name must contain only letters";
  }
  if (!accountHolderName.trim()) {
    return "Please enter account holder name";
  }
  if (!ALPHA_NAME_REGEX.test(accountHolderName.trim())) {
    return "Account holder name must contain only letters";
  }
  if (!file) {
    return "Please upload UPI QR code image";
  }
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return "QR code must be a PNG, JPG, JPEG, or WEBP image";
  }
  return "";
};

export default function NewUpiId() {
  const dispatch = useDispatch();
  const payment = useSelector((state) => state.payment);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [upiId, setUpiId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [file, setFile] = useState(null);
  const [localError, setLocalError] = useState("");

  const closeModal = () => {
    dispatch(closeAddUpiModal());
  };

  const addNewUpi = (e) => {
    e.preventDefault();
    dispatch(reset());

    const validationError = validateForm({
      upiId,
      accountName,
      accountHolderName,
      qrCodeUrl,
      file,
    });
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError("");
    dispatch(
      addUpiDetails({
        upi_id: upiId.trim(),
        account_name: accountName.trim() || undefined,
        account_holder_name: accountHolderName.trim() || undefined,
        qr_code_url: qrCodeUrl.trim() || undefined,
        file: file || null,
      })
    );
  };

  const handleUpiIdChange = (e) => {
    setLocalError("");
    setUpiId(e.target.value.replace(/\s/g, ""));
  };

  const handleAccountNameChange = (e) => {
    setLocalError("");
    setAccountName(e.target.value.replace(/[^A-Za-z\s]/g, ""));
  };

  const handleAccountHolderNameChange = (e) => {
    setLocalError("");
    setAccountHolderName(e.target.value.replace(/[^A-Za-z\s]/g, ""));
  };

  const handleQrCodeUrlChange = (e) => {
    setLocalError("");
    setQrCodeUrl(e.target.value);
  };

  const handleFileChange = (e) => {
    setLocalError("");
    const selected = e.target.files?.[0] || null;
    setFile(selected);
  };

  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  return (
    <div
      className="modal fade show new-account"
      style={{
        display: "block",
        backdropFilter: "blur(15px)",
        background: "rgb(70, 70, 70, 0.6)",
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title fs-5">Add UPI ID</h2>
            <i onClick={closeModal} className="bi bi-x-lg"></i>
          </div>
          <div className="modal-body p-4">
            <form className="needs-validation" onSubmit={addNewUpi}>
              {payment.error || localError ? (
                <div
                  className="mt-4 p-3 border border-light border-opacity-25 rounded mb-3"
                  style={{
                    color: "rgb(255, 120, 120)",
                    background: "rgb(20,20,20)",
                  }}
                >
                  <p className="text--medium mb-0">
                    <i className="bi bi-exclamation-circle"></i> Error:{" "}
                    {payment.error || localError}
                  </p>
                </div>
              ) : null}
              <div className="form">
                <div className="row">
                  <div className="col-md-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      UPI ID
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter UPI ID (e.g. user@upi)"
                      value={upiId}
                      className="form-control"
                      onChange={handleUpiIdChange}
                      required
                    />
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-md-6 col-sm-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      Account Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Account Name"
                      value={accountName}
                      className="form-control"
                      onChange={handleAccountNameChange}
                    />
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      Account Holder Name
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Account Holder Name"
                      value={accountHolderName}
                      className="form-control"
                      onChange={handleAccountHolderNameChange}
                      required
                    />
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-md-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      Existing QR Code URL (optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Enter QR Code URL"
                      value={qrCodeUrl}
                      className="form-control"
                      onChange={handleQrCodeUrlChange}
                    />
                  </div>
                </div>
                <div className="row mt-4 document-upload-section">
                  <div className="col-md-12">
                    <label
                      className="text--medium mb-3"
                      style={{ fontWeight: "200" }}
                    >
                      UPI QR code image file (PNG, JPG, JPEG, WEBP)
                      <span aria-hidden="true" className="required-asterisk">
                        {" *"}
                      </span>
                    </label>
                    <div className="file-input-div">
                      <span className="text--medium text--light">
                        {file
                          ? file.name
                          : "Choose file"}
                      </span>
                      <input
                        className="file"
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                        onChange={handleFileChange}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-end">
                <button
                  className={`standard-btn add-new-bank mt-4 mb-2 ${
                    isMobile ? "w-100" : "w-50"
                  }`}
                  type="submit"
                >
                  Add UPI ID
                </button>
                <p
                  className="text--medium"
                  style={{ fontWeight: "300", color: "#E42D2D" }}
                >
                  Note: UPI ID will be available after verification
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
